import React, { useEffect, useMemo, useState } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { DateRange, Granularity } from '../../types/Dashboard';
import { Venta } from '../../types/Altas';
import altasService from '../../services/altasService';
import { bucketize, formatDateAR, today } from './dashboardHelpers';
import { centerTotalPlugin } from './PlanMixChart';
import {
  rangoCampana,
  ventasEnRango,
  ejecutivasPorVentas,
  planesPorVentas,
  contarPor,
  ventasPorBucket,
  tablaEjecutivaPlan,
  ultimos7Dias,
  totalPlanes,
} from './ventasHelpers';
import { Panel, StatCard, ShareBar, Card, Badge } from '../ui';
import { inputClass } from '../altas/altasUi';
import {
  planColor,
  formatNumber,
  legendStyle,
  tooltipStyle,
  tickStyle,
  axisTitleStyle,
  yGrid,
} from './dashboardUi';

ChartJS.register(...registerables);

interface Props {
  range: DateRange;
  granularity: Granularity;
}

const GRAN_LABEL: Record<Granularity, string> = {
  day: 'día',
  week: 'semana',
  month: 'mes',
};

/**
 * Colores por ejecutiva. Distintos a los de los planes a propósito, para que
 * nadie lea "la barra azul" como Executive 3000 en este gráfico.
 */
const EJECUTIVA_COLORS = [
  '#2563eb',
  '#f59e0b',
  '#10b981',
  '#ec4899',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#64748b',
];
const ejecutivaColor = (i: number) => EJECUTIVA_COLORS[i % EJECUTIVA_COLORS.length];

const th = 'pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400';

/**
 * Sección "Ventas por ejecutiva" al final del dashboard. Cuenta solo las
 * altas confirmadas contra Salesforce y usa los mismos filtros de rango y
 * detalle que el resto de la página. Sin nombres ni DNI: solo totales.
 */
const VentasEjecutivas: React.FC<Props> = ({ range, granularity }) => {
  const [ventas, setVentas] = useState<Venta[] | null>(null);
  const [faltan, setFaltan] = useState({ pendientes: 0, planDistinto: 0 });
  const [campanaDesde, setCampanaDesde] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ejecutivaFiltro, setEjecutivaFiltro] = useState('');

  const todasLasEjecutivas = useMemo(() => (ventas ? ejecutivasPorVentas(ventas) : []), [ventas]);

  useEffect(() => {
    let cancelado = false;
    altasService
      .ventas()
      .then((r) => {
        if (cancelado) return;
        setVentas(r.ventas);
        setCampanaDesde(r.campanaDesde);
        setFaltan({ pendientes: r.pendientes, planDistinto: r.planDistinto });
      })
      .catch((err) => {
        if (!cancelado)
          setError(err instanceof Error ? err.message : 'No se pudieron cargar las ventas');
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const calc = useMemo(() => {
    if (!ventas || !campanaDesde) return null;
    const enRango = ventasEnRango(ventas, range, campanaDesde).filter(
      (v) => ejecutivaFiltro === '' || v.ejecutiva === ejecutivaFiltro,
    );
    const ejecutivas = ejecutivasPorVentas(enRango);
    const planes = planesPorVentas(enRango);
    const buckets = bucketize(rangoCampana(range, campanaDesde), granularity);
    const porBucket = ventasPorBucket(enRango, buckets);
    const porPlan = contarPor(enRango, (v) => v.plan);

    const datasets = ejecutivas.map((ejecutiva, i) => ({
      label: ejecutiva,
      data: porBucket.map((b) => b[ejecutiva] ?? 0),
      backgroundColor: ejecutivaColor(i),
      stack: 'ventas',
      borderRadius: 3,
      maxBarThickness: 34,
    }));

    // Con una ejecutiva elegida la torta pasa a mostrar sus ventas por plan:
    // una sola porción no dice nada.
    const porEjecutiva = contarPor(enRango, (v) => v.ejecutiva);
    const torta = ejecutivaFiltro
      ? {
          titulo: `Ventas de ${ejecutivaFiltro} por plan`,
          labels: planes,
          data: planes.map((p) => porPlan[p]),
          colors: planes.map((p) => planColor(p)),
        }
      : {
          titulo: 'Participación por ejecutivo',
          labels: ejecutivas,
          data: ejecutivas.map((e) => porEjecutiva[e]),
          colors: ejecutivas.map((_, i) => ejecutivaColor(i)),
        };

    const semana = ultimos7Dias(
      ventas.filter((v) => ejecutivaFiltro === '' || v.ejecutiva === ejecutivaFiltro),
      today(),
    );
    return {
      semana,
      torta,
      total: totalPlanes(enRango),
      ejecutivas,
      planes,
      porPlan,
      tabla: tablaEjecutivaPlan(enRango),
      chart: { labels: buckets.map((b) => b.label), datasets },
      promedio: buckets.length > 0 ? totalPlanes(enRango) / buckets.length : 0,
      buckets: buckets.length,
    };
  }, [ventas, campanaDesde, range, granularity, ejecutivaFiltro]);

  const sinConfirmar = faltan.pendientes + faltan.planDistinto;

  // Como en el mix de planes: el total viaja en las opciones, no en el cierre del plugin.
  const total = calc?.total ?? 0;
  const tortaOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      centerTotal: { value: formatNumber(total), label: 'ventas' },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          label: (ctx: { parsed: number }) =>
            ` ${formatNumber(ctx.parsed)} ventas · ${total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0'}%`,
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-poppins text-xl font-semibold text-gappex-secondary">
            Ventas por ejecutivo
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Altas cargadas por el equipo comercial y confirmadas contra Salesforce. Mismo rango y
            detalle que arriba
            {campanaDesde !== null &&
              range.from < campanaDesde &&
              `, desde el inicio de la campaña (${formatDateAR(campanaDesde)})`}
            .
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <select
            value={ejecutivaFiltro}
            onChange={(e) => setEjecutivaFiltro(e.target.value)}
            aria-label="Filtrar por ejecutivo"
            className={`${inputClass} w-auto py-1.5`}
            disabled={todasLasEjecutivas.length === 0}
          >
            <option value="">Todos los ejecutivos</option>
            {todasLasEjecutivas.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          {sinConfirmar > 0 && (
            <Link to="/altas" className="shrink-0">
              <Badge
                tone="warning"
                title="Altas que todavía no se confirmaron contra Salesforce. No se cuentan hasta que se confirmen."
              >
                {formatNumber(sinConfirmar)} sin confirmar
              </Badge>
            </Link>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {!calc ? (
        !error && (
          <Card className="animate-pulse p-6">
            <div className="h-40 rounded-xl bg-slate-100" />
          </Card>
        )
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Ventas en el rango"
              value={formatNumber(calc.total)}
              sub="Altas confirmadas"
              tone="positive"
            />
            <StatCard
              label="Ventas últimos 7 días"
              value={formatNumber(calc.semana.actual)}
              sub="Confirmadas en Salesforce"
              tone="info"
              title="Hoy y los 6 días anteriores, sin importar el rango elegido arriba."
            />
            <StatCard
              label="Plan más vendido"
              value={calc.planes[0] ?? '—'}
              sub={
                calc.planes[0]
                  ? `${formatNumber(calc.porPlan[calc.planes[0]])} ventas`
                  : 'Sin ventas en el rango'
              }
              tone="default"
            />
            <StatCard
              label={`Promedio por ${GRAN_LABEL[granularity]}`}
              value={calc.promedio.toLocaleString('es-AR', {
                maximumFractionDigits: 1,
              })}
              tone="default"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Panel
              title={`Ventas por ${GRAN_LABEL[granularity]}`}
              subtitle="Cada barra es un período; los colores, los ejecutivos."
              className="lg:col-span-2"
            >
              {calc.total === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">
                  No hay ventas confirmadas en este rango.
                </p>
              ) : (
                <div className="h-72">
                  <Bar
                    data={calc.chart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: legendStyle, tooltip: tooltipStyle },
                      scales: {
                        x: {
                          stacked: true,
                          grid: { display: false },
                          ticks: tickStyle,
                        },
                        y: {
                          stacked: true,
                          beginAtZero: true,
                          grid: yGrid,
                          ticks: { ...tickStyle, precision: 0 },
                          title: {
                            display: true,
                            text: 'Ventas',
                            ...axisTitleStyle,
                          },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </Panel>

            <Panel
              title={calc.torta.titulo}
              subtitle="Qué parte del total del rango es de cada una."
            >
              {calc.total === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">Sin ventas en el rango.</p>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="h-52 w-52">
                    <Doughnut
                      data={{
                        labels: calc.torta.labels,
                        datasets: [
                          {
                            data: calc.torta.data,
                            backgroundColor: calc.torta.colors,
                            borderWidth: 3,
                            borderColor: '#fff',
                            hoverOffset: 6,
                            hoverBorderColor: '#fff',
                          },
                        ],
                      }}
                      options={tortaOptions}
                      plugins={[centerTotalPlugin]}
                    />
                  </div>
                  <ul className="w-full space-y-1 text-xs">
                    {calc.torta.labels.map((label, i) => (
                      <li key={label} className="flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-2 text-slate-600">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: calc.torta.colors[i] }}
                          />
                          <span className="truncate">{label}</span>
                        </span>
                        <span className="tabular-nums text-slate-500">
                          {formatNumber(calc.torta.data[i])} ·{' '}
                          {((calc.torta.data[i] / calc.total) * 100).toFixed(0)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Panel>

            <Panel
              title="Por ejecutivo y plan"
              subtitle="Totales del rango, de mayor a menor."
              className="lg:col-span-3"
            >
              {calc.tabla.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Sin ventas en el rango.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className={`${th} pe-3`}>Ejecutivo</th>
                      <th className={`${th} pe-3 text-right`}>Ventas</th>
                      <th className={th}>Por plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.tabla.map((fila, i) => (
                      <tr key={fila.ejecutiva} className="border-b border-slate-100 last:border-0">
                        <td className="py-2 pe-3">
                          <span className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: ejecutivaColor(i) }}
                            />
                            <span className="font-medium text-gappex-secondary">
                              {fila.ejecutiva}
                            </span>
                          </span>
                          <ShareBar
                            pct={(fila.total / calc.total) * 100}
                            color={ejecutivaColor(i)}
                            className="mt-1.5"
                          />
                        </td>
                        <td className="py-2 pe-3 text-right align-top font-medium tabular-nums text-gappex-secondary">
                          {formatNumber(fila.total)}
                        </td>
                        <td className="py-2 align-top">
                          <div className="flex flex-wrap gap-1">
                            {calc.planes
                              .filter((p) => fila.porPlan[p])
                              .map((p) => (
                                <span
                                  key={p}
                                  className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-600"
                                  title={p}
                                >
                                  <span
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{ backgroundColor: planColor(p) }}
                                  />
                                  {p.replace(/^\D+/, '')} · {fila.porPlan[p]}
                                </span>
                              ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 font-semibold text-gappex-secondary">
                      <td className="pt-3 pe-3">Total</td>
                      <td className="pt-3 pe-3 text-right tabular-nums">
                        {formatNumber(calc.total)}
                      </td>
                      <td className="pt-3 text-[11px] font-normal tabular-nums text-slate-400">
                        {calc.planes
                          .map((p) => `${p.replace(/^\D+/, '')} · ${calc.porPlan[p]}`)
                          .join('  ')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
};

export default VentasEjecutivas;
